import React, { useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { TextField } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { GoalCreationWizardContext } from '../goal-creation-wizard-context';
import { getStepStyles } from '../../../conversion-wizard-components/src/shared-styles/shared-content-style';
import { validateNumeric, validateRange } from '../shared/validation-util';

const input2Duration = timeObj => ((parseInt(timeObj.hour, 10) * 60) + (parseInt(timeObj.min, 10) * 60) + parseInt(timeObj.sec, 10));

const duration2Input = (timeInSec) => {
  const timeObj = {};
  timeObj.sec = timeInSec % 60;
  const remainingMin = parseInt(timeInSec / 60, 10);
  timeObj.min = remainingMin % 60;
  timeObj.hour = parseInt(remainingMin / 60, 10);

  return timeObj;
};

const DurationSettingUnstyled = (props) => {
  const {
    classes,
    updateGoalProps,
    showError,
    resetShowError,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const [timeObj, setTimeObj] = useState(duration2Input(_.isUndefined(wizardContext.goalContext.Duration) ? 0 : wizardContext.goalContext.Duration));
  const [shouldShowError, setShouldShowError] = useState(false);

  useEffect(() => {
    if (showError) {
      resetShowError();
    }
  }, [resetShowError, showError]);

  const onChangeTimeInput = useCallback((event) => {
    const timeKey = event.target.title;
    const inputValue = event.target.value;

    if (validateNumeric(inputValue) || inputValue.length === 0) {
      const timeValue = parseInt(inputValue, 10);
      const updatedObj = { [timeKey]: timeValue };

      setTimeObj({ ...timeObj, ...updatedObj });

      const updatedTime = input2Duration({ ...timeObj, ...updatedObj });
      if (validateRange(timeKey, timeValue)) {
        setShouldShowError(false);
        updateGoalProps({ Duration: updatedTime });
      } else {
        setShouldShowError(true);
        updateGoalProps({ Duration: undefined });
      }
    }
  }, [timeObj, updateGoalProps]);

  return (
    <div className="duration-setting">
      <div className={classes.nonAccordionTitle}>
        <span className="title">{i18n.getString(_TL_('Duration'))}</span>
        <HelpButton
          aria-label={i18n.getString(_TL_('Duration'))}
          topic="pop_BA_UET_Duration"
        />
      </div>
      <p className={classes.BodyText}>{i18n.getString(_TL_('Greater than'))}</p>
      <div className="row">
        <div className="row col-sm-8">
          <div className="col-sm-2 hour">
            <TextField
              ariaLabel="hour"
              title="hour"
              value={_.isNaN(timeObj.hour) ? '' : timeObj.hour}
              onChange={onChangeTimeInput}
            />
            <div>{i18n.getString(_TL_('Hour'))}</div>
          </div>
          <div className="col-sm-2 min">
            <TextField
              ariaLabel="min"
              title="min"
              value={_.isNaN(timeObj.min) ? '' : timeObj.min}
              onChange={onChangeTimeInput}
            />
            <div>{i18n.getString(_TL_('Min'))}</div>
          </div>
          <div className="col-sm-2 sec">
            <TextField
              ariaLabel="sec"
              title="sec"
              value={_.isNaN(timeObj.sec) ? '' : timeObj.sec}
              onChange={onChangeTimeInput}
            />
            <div>{i18n.getString(_TL_('Sec'))}</div>
          </div>
        </div>
      </div>
      {shouldShowError &&
      <div className="row" role="alert">
        <p className={classes.errorText}>{i18n.getString(_TL_('Enter a valid duration period.'))}</p>
      </div>}
    </div>
  );
};

DurationSettingUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  updateGoalProps: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
};

export const DurationSetting = withDefaultStyles(DurationSettingUnstyled, getStepStyles);
